import { Overline, Tag, cx } from "@amber/ui";
import {
  FIELD_STATUS_LABELS,
  IDENTITY_FIELD_LABELS,
  OPTIONAL_FIELDS,
  type IdentityProposal,
  type OptionalField,
  type ProposedField,
} from "../../contracts/index";
import { CandidatePortrait } from "./CandidatePortrait";

function statusTone(status: ProposedField["status"]) {
  if (status === "sourced") return "neutral";
  if (status === "conflict") return "waiting";
  return "quiet";
}

function StatusTag({ field }: { field: ProposedField }) {
  const tone = statusTone(field.status);
  return <Tag tone={tone}>{FIELD_STATUS_LABELS[field.status]}</Tag>;
}

export function ProposalView({
  proposal,
  accept,
  setAccept,
}: {
  proposal: IdentityProposal;
  accept: Record<OptionalField, boolean>;
  setAccept: (next: Record<OptionalField, boolean>) => void;
}) {
  const shownEditions = proposal.linked_editions.slice(0, 6);
  return (
    <div className="mt-3 rounded-xl border border-divider">
      <div className="flex items-center gap-3 border-b border-divider bg-surface-2 px-3 py-2.5">
        <CandidatePortrait name={proposal.name} url={proposal.image_url} size="lg" />
        <div className="min-w-0">
          <b className="block text-base font-semibold text-fg">{proposal.name}</b>
          <span className="text-md text-muted">Proposed from {proposal.source.edition}.wikipedia.org</span>
          <div className="mt-1 flex flex-wrap gap-1">
            {shownEditions.map((e) => (
              <Tag key={e} tone={e === proposal.source.edition ? "accent" : "quiet"}>
                {e}
              </Tag>
            ))}
            {proposal.linked_editions.length > shownEditions.length ? (
              <Tag tone="quiet">+{proposal.linked_editions.length - shownEditions.length}</Tag>
            ) : null}
          </div>
        </div>
      </div>
      <ul className="m-0 list-none p-0">
        {proposal.fields
          .filter((f) => f.field !== "description")
          .map((f) => {
            const optional = (OPTIONAL_FIELDS as readonly string[]).includes(f.field)
              ? (f.field as OptionalField)
              : null;
            return (
              <li
                key={f.field}
                className="grid grid-cols-[18px_84px_minmax(0,1fr)] items-start gap-2 border-b border-divider px-3 py-2 last:border-b-0"
              >
                <span>
                  {optional && f.status === "sourced" ? (
                    <input
                      type="checkbox"
                      aria-label={`Keep ${IDENTITY_FIELD_LABELS[f.field]}`}
                      className="mt-0.5 accent-[var(--color-link)]"
                      checked={accept[optional]}
                      onChange={(e) => {
                        setAccept({ ...accept, [optional]: e.target.checked });
                      }}
                    />
                  ) : null}
                </span>
                <span className="text-muted">{IDENTITY_FIELD_LABELS[f.field]}</span>
                <span className="min-w-0">
                  <span className="flex items-start justify-between gap-2">
                    <span className={cx("min-w-0", f.value ? "text-fg" : "text-muted")}>
                      {f.value ?? (f.status === "conflict" ? f.values.map((v) => v.value).join(" or ") : "Unknown")}
                    </span>
                    <StatusTag field={f} />
                  </span>
                  <span className="block text-xs text-muted">
                    {f.evidence_url ? (
                      <a
                        href={f.evidence_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-muted underline decoration-dotted"
                      >
                        {f.evidence}
                      </a>
                    ) : (
                      f.evidence
                    )}
                  </span>
                </span>
              </li>
            );
          })}
      </ul>
      {proposal.open_questions.length > 0 ? (
        <div className="border-t border-divider px-3 py-2">
          <Overline as="b">Missing or conflicting</Overline>
          <ul className="m-0 mt-1 list-disc pl-4 text-md text-muted">
            {proposal.open_questions.map((q) => (
              <li key={`${q.field}-${q.detail}`}>{q.detail}</li>
            ))}
          </ul>
          <p className="m-0 mt-1 text-xs text-muted">Stays visible on the profile until someone confirms a value.</p>
        </div>
      ) : null}
    </div>
  );
}
